import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, Sparkles } from 'lucide-react';
import { images, restaurantInfo } from '../data/mock';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Etusivu' },
    { path: '/lounas', label: 'Lounas' },
    { path: '/kahvila', label: 'Kahvila' },
    { path: '/pitopalvelu', label: 'Pitopalvelu' },
    { path: '/pikkujoulut', label: 'Pikkujoulut' },
    { path: '/tarina', label: 'Tarina' },
    { path: '/yhteystiedot', label: 'Yhteystiedot' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-white sticky top-0 z-50 shadow-sm">
      {/* Top bar with contact info - playful design */}
      <div className="hidden md:block bg-gradient-to-r from-emerald-50 via-amber-50 to-emerald-50 border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-6 py-2 flex justify-between items-center text-sm">
          <div className="flex items-center gap-2 text-emerald-600">
            <Sparkles className="w-4 h-4" />
            <span className="font-handwritten text-lg">Tervetuloa!</span>
          </div>
          <div className="flex items-center gap-6 text-stone-600">
            <a href={`tel:${restaurantInfo.phone}`} className="flex items-center gap-2 hover:text-emerald-600 transition-colors">
              <Phone className="w-4 h-4" />
              {restaurantInfo.phone}
            </a>
            <a href={`mailto:${restaurantInfo.email}`} className="flex items-center gap-2 hover:text-emerald-600 transition-colors">
              <Mail className="w-4 h-4" />
              {restaurantInfo.email}
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="max-w-7xl mx-auto px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img 
              src={images.logo} 
              alt="Ravintola Data" 
              className="h-24 md:h-28 w-auto transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-xl text-base font-medium transition-all relative ${
                  isActive(link.path)
                    ? 'text-emerald-600 bg-emerald-50'
                    : 'text-stone-700 hover:text-emerald-600 hover:bg-emerald-50/50'
                }`}
              >
                {link.label}
                {isActive(link.path) && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                )}
              </Link>
            ))}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-stone-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all"
            aria-label="Avaa valikko"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isMenuOpen ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="flex flex-col gap-1 py-4 border-t border-emerald-100">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`px-4 py-3 rounded-xl text-base font-medium transition-all ${
                  isActive(link.path)
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-stone-700 hover:bg-emerald-50 hover:text-emerald-600'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {/* Mobile contact info */}
            <div className="mt-4 pt-4 border-t border-emerald-100 px-4 flex flex-col gap-3 text-sm text-stone-600">
              <a href={`tel:${restaurantInfo.phone}`} className="flex items-center gap-2 hover:text-emerald-600">
                <Phone className="w-4 h-4" />
                {restaurantInfo.phone}
              </a>
              <a href={`mailto:${restaurantInfo.email}`} className="flex items-center gap-2 hover:text-emerald-600">
                <Mail className="w-4 h-4" />
                {restaurantInfo.email}
              </a>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
